import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { ArrowRight, Zap, Shield, Globe, BarChart3, Users, Sparkles } from "lucide-react";
import { Button } from "../ui/Button";
import { Link } from "react-router-dom";
import Badge from "../ui/Badge";

const Section = styled.section`
  padding: 96px 16px;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    padding: 96px 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: 120px 32px;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 64px;
`;

const BadgeWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
`;

const Title = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-size: ${({ theme }) => theme.typography.fontSize.h2};
  font-weight: 700;
  line-height: 1.1;
  margin: 0 0 16px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const GradientTitle = styled.span`
  background: ${({ theme }) => theme.colors.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Description = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.bodyLarge};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};
  max-width: 640px;
  margin: 0 auto;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  margin-bottom: 48px;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 32px;
  }
`;

const FeatureCard = styled.div`
  padding: 32px;
  border-radius: 24px;
  background: ${({ theme }) => theme.colors.light?.card?.background || theme.colors.light?.background || "white"};
  border: 1px solid ${({ theme }) => theme.colors.light?.border || "hsl(240, 10%, 90%)"};
  transition: all 0.5s ease-out;
  cursor: pointer;

  body.dark & {
    background: ${({ theme }) => theme.colors.dark?.card?.background || theme.colors.dark?.background || "hsl(240, 15%, 10%)"};
    border-color: ${({ theme }) => theme.colors.dark?.border || "hsl(240, 15%, 18%)"};
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
    border-color: ${({ theme }) => theme.colors.light?.primary || theme.colors.primary.light}30;

    body.dark & {
      border-color: ${({ theme }) => theme.colors.dark?.primary || theme.colors.primary.light}30;
    }
  }
`;

const IconBox = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: ${({ $gradient }) => $gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  color: white;
`;

const FeatureTitle = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-size: ${({ theme }) => theme.typography.fontSize.h3};
  font-weight: 600;
  margin: 0 0 12px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const FeatureDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};
  margin: 0 0 16px 0;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const LearnMore = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.light?.primary || theme.colors.primary.light};
  transition: gap 0.3s ease;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.primary || theme.colors.primary.light};
  }

  ${FeatureCard}:hover & {
    gap: 12px;
  }
`;

const CTAWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const featuresConfig = [
  { icon: Zap, titleKey: "landing.features.lightningPerformance", descKey: "landing.features.lightningPerformanceDesc", gradient: "linear-gradient(to bottom right, hsl(48, 96%, 53%), hsl(25, 95%, 53%))" },
  { icon: Shield, titleKey: "landing.features.enterpriseSecurity", descKey: "landing.features.enterpriseSecurityDesc", gradient: "linear-gradient(to bottom right, hsl(142, 76%, 36%), hsl(160, 84%, 39%))" },
  { icon: Globe, titleKey: "landing.features.globalScale", descKey: "landing.features.globalScaleDesc", gradient: "linear-gradient(to bottom right, hsl(217, 91%, 60%), hsl(199, 89%, 48%))" },
  { icon: BarChart3, titleKey: "landing.features.advancedAnalytics", descKey: "landing.features.advancedAnalyticsDesc", gradient: "linear-gradient(to bottom right, hsl(262, 83%, 58%), hsl(330, 81%, 60%))" },
  { icon: Users, titleKey: "landing.features.teamCollaboration", descKey: "landing.features.teamCollaborationDesc", gradient: "linear-gradient(to bottom right, hsl(330, 81%, 60%), hsl(350, 89%, 60%))" },
  { icon: Sparkles, titleKey: "landing.features.smartAutomation", descKey: "landing.features.smartAutomationDesc", gradient: "linear-gradient(to bottom right, hsl(239, 84%, 67%), hsl(258, 90%, 66%))" },
];

export function FeaturesSection() {
  const { t } = useTranslation();
  return (
    <Section>
      <Container>
        <Header>
          <BadgeWrapper>
            <Badge $variant="primary">{t("landing.features.badge")}</Badge>
          </BadgeWrapper>
          <Title>
            {t("landing.features.title")} <GradientTitle>{t("landing.features.titleHighlight")}</GradientTitle>
          </Title>
          <Description>{t("landing.features.description")}</Description>
        </Header>

        <FeaturesGrid>
          {featuresConfig.map((feature) => {
            const Icon = feature.icon;
            return (
              <FeatureCard key={feature.titleKey}>
                <IconBox $gradient={feature.gradient}>
                  <Icon size={24} />
                </IconBox>
                <FeatureTitle>{t(feature.titleKey)}</FeatureTitle>
                <FeatureDescription>{t(feature.descKey)}</FeatureDescription>
                <LearnMore>
                  {t("landing.features.learnMore")} <ArrowRight size={16} />
                </LearnMore>
              </FeatureCard>
            );
          })}
        </FeaturesGrid>

        <CTAWrapper>
          <Link to="/features" style={{ textDecoration: "none" }}>
            <Button variant="gradient" size="lg">
              {t("landing.features.exploreAllFeatures")}
              <ArrowRight size={20} />
            </Button>
          </Link>
        </CTAWrapper>
      </Container>
    </Section>
  );
}
